import React from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { FormikProps } from 'formik';
import { ContentSpacer } from '../layout/generic-content-spacer';
import { buttonStyles } from '../../constants/theme/styles/components/buttonstyles';
import { WysiwygWithReactLink } from '../wysiwyg-with-react-link';
import { replaceAll } from '../../helpers/string-helpers';

interface FormikErrorDisplayProps {
	formikProps: FormikProps<any>;
	formKeys?: string[];
	onCancel: () => any;
}

interface ErrorMappingProps {
	errorKey: string;
	error: any;
	parentKey: string;
	handleErrorClick: any;
}

function ErrorMapping(props: ErrorMappingProps) {
	const { errorKey, error, parentKey, handleErrorClick } = props;
	const classes = buttonStyles();

	if (!error[errorKey]) {
		return null;
	}

	if (Array.isArray(error[errorKey])) {
		return (
			<>
				{
					(error[errorKey] as any[]).map((err, idx) => {
						if (!err) {
							return null;
						}
						const nestedKeys = Object.keys(err);

						return nestedKeys.map((k, subIdx) => {
							return (
								<ErrorMapping
									key={`${errorKey}-${subIdx}`}
									errorKey={k}
									error={err}
									parentKey={`${parentKey ? `${parentKey}.` : ''}${errorKey}[${idx}]`}
									handleErrorClick={handleErrorClick}
								/>
							);
						});
					})
				}
			</>
		);
	}

	if (typeof error[errorKey] === 'object') {
		return (
			<>
				{
					Object.keys(error[errorKey])?.map((k, idx) => {
						return (
							<ErrorMapping
								key={`${errorKey}-${idx}`}
								errorKey={k}
								error={error[errorKey][k]}
								parentKey={`${parentKey ? `${parentKey}.` : ''}${errorKey}`}
								handleErrorClick={handleErrorClick}
							/>
						);
					})
				}
			</>
		);
	}

	if (typeof error[errorKey] === 'string' || error[errorKey] instanceof String) {
		return (
			<li key={`${parentKey ? `${parentKey}.` : ''}${errorKey}`}>
				<button type='button' onClick={() => handleErrorClick(`${parentKey ? `${parentKey}.` : ''}${errorKey}`)} className={classes.linkButton}>
					<WysiwygWithReactLink content={error[errorKey]} />
				</button>
			</li>
		);
	}

	return null;
}

export function FormikErrorSummary(props: FormikErrorDisplayProps) {
	const { errors, touched, values } = props.formikProps;

	const filteredKeys: { [key: string]: string } = {};

	const fieldNames = props.formKeys || Object.keys(values);

	// only want to display errors for touched fields or non field specific 
	let errorKeys = Object.keys(errors).filter(key => !fieldNames.includes(key) || touched[key]);

	if (errorKeys.length < 1) return null;

	errorKeys.forEach(
		a =>{
			if (typeof errors[a] === 'string'){
				filteredKeys[errors[a] as string] = a; 
				return;
			}
			
			filteredKeys[a] = a; 
		});
	
	errorKeys = Object.keys(filteredKeys).map(a => filteredKeys[a]);

	const handleErrorClick = (key: string) => {
		const scrollOptions: ScrollIntoViewOptions = {
			behavior: 'smooth',
			block: 'center',
		};

		const element = document.querySelector(`[data-formdata="${key}"]`) || document.querySelector(`[data-formdata="${replaceAll(replaceAll(key, '[', '.'), ']', '')}"]`) || document.querySelector(`[name="${key}"]`);
		if (element) {
			if (element.previousElementSibling) {
				element.previousElementSibling.scrollIntoView(scrollOptions);
			} else {
				element.scrollIntoView(scrollOptions);
			}
		} else if (props.onCancel) {
			props.onCancel();

			// Try to go back and try again.
			setTimeout(
				() => {
					const elementBack = document.querySelector(`[data-formdata="${key}"]`) || document.querySelector(`[name="${key}"]`);
					if (elementBack) {
						if (elementBack.previousElementSibling) {
							elementBack.previousElementSibling.scrollIntoView(scrollOptions);
						} else {
							elementBack.scrollIntoView(scrollOptions);
						}
					}
				}, 1000);
		}
	};

	return (
		errorKeys.length > 0 ? (
		<ContentSpacer>
			<Banner
				banner={{
					type: 'error',
					label: <p><strong>{`${errorKeys.length} error(s) were found in this form.`}</strong></p>,
					description: <ul>
						{errorKeys.map((key, idx) => {
							if (!errors[key]) {
								return null;
							}
							if (!fieldNames.includes(key)) {
								return (
									<li key={key}>
										<p><WysiwygWithReactLink content={errors[key] as string} /></p>
									</li>
								);
							}

							return (
								<ErrorMapping
									key={`${key}-${idx}`}
									errorKey={key}
									error={errors}
									parentKey={null}
									handleErrorClick={handleErrorClick}
								/>
							);
						})}
					</ul>,
				}}
			/>

			</ContentSpacer>) : null
	);
}

FormikErrorSummary.defaultProps = {
	formKeys: null,
};